import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import api from '../api/api';
import '../styles/pages/MoodDetail.css';

/**
 * MoodDetail - 気分記録の詳細（1ヶ月推移グラフ）
 * MYPAGE_REDESIGN_V2.md 3-1. 気分の推移グラフ
 */
const MoodDetail = () => {
  const navigate = useNavigate();
  const { depressionMode } = useDepressionMode();
  const [loading, setLoading] = useState(true);
  const [monthData, setMonthData] = useState(null);
  const [aiInsight, setAiInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(true);

  // ページタイトル設定
  useEffect(() => {
    document.title = '気分の推移 | つむぎ';
  }, []);

  useEffect(() => {
    loadMonthData();
    loadAiInsight();
  }, []);

  const loadMonthData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/mood/monthly');
      if (response.data.success) {
        setMonthData(response.data.monthData);
      }
    } catch (error) {
      console.error('月間データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAiInsight = async () => {
    try {
      setInsightLoading(true);
      const response = await api.get('/api/mood/ai-insight');
      if (response.data.success) {
        setAiInsight(response.data.insight);
      }
    } catch (error) {
      console.error('AIインサイト取得エラー:', error);
    } finally {
      setInsightLoading(false);
    }
  };

  const getMoodImage = (mood) => {
    switch (mood) {
      case 'good': return '/emote/alright.png';
      case 'normal': return '/emote/normal.png';
      case 'tired': return '/emote/tiring.png';
      default: return null;
    }
  };

  const getMoodLabel = (mood) => {
    switch (mood) {
      case 'good': return '元気';
      case 'normal': return '普通';
      case 'tired': return '疲れた';
      default: return '未記録';
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <PageTitle
          title="気分の推移"
          subtitle="読み込み中..."
          icon="📈"
        />
        <div className="tsumugi-container py-6">
          <div className="tsumugi-card text-center">
            <p className="text-hint">読み込み中...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!monthData) {
    return (
      <AppLayout>
        <PageTitle
          title="気分の推移"
          subtitle="データがありません"
          icon="📈"
        />
      </AppLayout>
    );
  }

  // グラフ用データ整形（5日ごとに表示）
  const displayDays = monthData.days.filter((_, index) => index % 5 === 0 || index === monthData.days.length - 1);

  return (
    <AppLayout>
      <PageTitle
        title="最近の様子"
        subtitle="あなたの30日間"
        icon="🌸"
        backPath="/mypage"
        backLabel="マイページ"
      />

      <div className="tsumugi-container py-6">
        {/* グラフカード - おやすみモード時は非表示 */}
        {!depressionMode && (
        <div className="tsumugi-card">
          <h3 className="tsumugi-card-title">📊 気分の記録</h3>

          {/* 折れ線グラフ */}
          <div className="mood-graph-wrapper">
            {/* Y軸ラベル */}
            <div className="mood-graph-y-labels">
              <span className="mood-graph-y-label">
                <img src="/emote/alright.png" alt="元気" />
              </span>
              <span className="mood-graph-y-label">
                <img src="/emote/normal.png" alt="普通" />
              </span>
              <span className="mood-graph-y-label">
                <img src="/emote/tiring.png" alt="疲れた" />
              </span>
            </div>

            {/* グラフエリア */}
            <div className="mood-graph-container">
              <svg className="mood-graph-svg" viewBox="0 0 300 120" preserveAspectRatio="xMidYMid meet">
                {/* グリッド線（上端・中央・下端 + padding考慮） */}
                <line x1="0" y1="10" x2="300" y2="10" stroke="var(--hint)" strokeWidth="0.3" opacity="0.3" />
                <line x1="0" y1="60" x2="300" y2="60" stroke="var(--hint)" strokeWidth="0.3" opacity="0.3" />
                <line x1="0" y1="110" x2="300" y2="110" stroke="var(--hint)" strokeWidth="0.3" opacity="0.3" />

                {/* グラデーション定義 */}
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--lilac)" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="var(--peach)" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="var(--mint)" stopOpacity="0.8" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>

                {/* 折れ線（細く洗練） */}
                <polyline
                  points={monthData.days.map((day, index) => {
                    if (!day.recorded) return null;
                    const x = (index / (monthData.days.length - 1)) * 300;
                    const y = day.mood === 'good' ? 10 : day.mood === 'normal' ? 60 : 110;
                    return `${x},${y}`;
                  }).filter(p => p !== null).join(' ')}
                  fill="none"
                  stroke="url(#lineGradient)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  filter="url(#glow)"
                />

                {/* データポイント（小さく洗練） */}
                {monthData.days.map((day, index) => {
                  if (!day.recorded) return null;
                  const x = (index / (monthData.days.length - 1)) * 300;
                  const y = day.mood === 'good' ? 10 : day.mood === 'normal' ? 60 : 110;
                  const color = day.mood === 'good' ? 'var(--mint)' : day.mood === 'normal' ? 'var(--peach)' : 'var(--lilac)';
                  return (
                    <circle
                      key={index}
                      cx={x}
                      cy={y}
                      r="2"
                      fill={color}
                      stroke="white"
                      strokeWidth="1.5"
                      opacity="0.95"
                    />
                  );
                })}
              </svg>
            </div>
          </div>

          {/* X軸ラベル（5日ごと + 今日） */}
          <div className="mood-graph-x-labels">
            {[0, 5, 10, 15, 20, 25, 29].map((dayIndex) => {
              const day = monthData.days[dayIndex];
              if (!day) return null;
              const date = new Date(day.date);
              const label = dayIndex === 29 ? '今日' : `${date.getMonth() + 1}/${date.getDate()}`;
              // SVGと同じ計算式で位置を合わせる
              const position = (dayIndex / (monthData.days.length - 1)) * 100;
              return (
                <span
                  key={dayIndex}
                  className="mood-graph-x-label"
                  style={{ position: 'absolute', left: `${position}%`, transform: 'translateX(-50%)' }}
                >
                  {label}
                </span>
              );
            })}
          </div>

          {/* 分析メッセージ */}
          <div className={`mood-graph-analysis ${monthData.improvement.trend === 'improving' ? 'mood-graph-analysis--improving' : ''}`}>
            <p className="mood-graph-analysis__text">
              💡 {monthData.improvement.message}
            </p>
          </div>
        </div>
        )}

        {/* おやすみモード時のメッセージ */}
        {depressionMode && (
          <div className="tsumugi-card">
            <div className="depression-mode-message">
              <span className="depression-mode-message__icon">🌙</span>
              <p className="depression-mode-message__text">
                今日はグラフを見なくても大丈夫。<br />
                あなたがここにいるだけで十分だよ。
              </p>
            </div>
          </div>
        )}

        {/* つむぎからの気づき（AIインサイト） */}
        {aiInsight && (
          <div className="tsumugi-card tsumugi-card-special">
            <h3 className="tsumugi-card-title">✨ つむぎからの気づき</h3>
            {insightLoading ? (
              <div className="ai-insight-loading">
                <p className="text-hint text-sm">
                  あなたのデータを見ているね...
                </p>
              </div>
            ) : (
              <div className="ai-insight-content">
                <div className="ai-insight-mascot">
                  <img src="/tsumugi.png" alt="つむぎ" />
                </div>
                <p className="ai-insight-message">{aiInsight}</p>
                <p className="ai-insight-note">
                  ※ 1-2週間ごとに、新しい気づきをお届けするね
                </p>
              </div>
            )}
          </div>
        )}

        {/* 統計カード（詳細表示） - おやすみモード時は非表示 */}
        {!depressionMode && (
        <div className="tsumugi-card">
          <h3 className="tsumugi-card-title">✨ 今月の記録</h3>

          <div className="mood-stats">
            {/* 記録日数（ポジティブ表現） */}
            <div className="mood-stats-record">
              <div className="mood-stats-record__header">
                <span className="mood-stats-record__label">記録できた日</span>
                <span className="mood-stats-record__value">
                  {monthData.recordedDays}日
                </span>
              </div>
              <div className="mood-stats-record__bar">
                <div
                  className="mood-stats-record__bar-fill"
                  style={{ width: `${(monthData.recordedDays / monthData.totalDays) * 100}%` }}
                ></div>
              </div>
              <p className="mood-stats-record__encouragement">
                {monthData.recordedDays >= 20 ? '素晴らしい！たくさん記録できたね✨' :
                 monthData.recordedDays >= 10 ? '良いペースで記録できてるよ♪' :
                 monthData.recordedDays >= 5 ? '少しずつ続けていこう' :
                 '無理せず、気が向いた時に記録してみてね'}
              </p>
            </div>

            {/* 気分の内訳（元気・普通を優先表示） */}
            <div className="mood-stats-breakdown">
              {monthData.moodCounts.good > 0 && (
                <div className="mood-stats-item mood-stats-item--good">
                  <div className="mood-stats-item__emoji">
                    <img src="/emote/alright.png" alt="元気" />
                  </div>
                  <div className="mood-stats-item__count">{monthData.moodCounts.good}日</div>
                  <div className="mood-stats-item__label">元気な日</div>
                </div>
              )}

              {monthData.moodCounts.normal > 0 && (
                <div className="mood-stats-item mood-stats-item--normal">
                  <div className="mood-stats-item__emoji">
                    <img src="/emote/normal.png" alt="普通" />
                  </div>
                  <div className="mood-stats-item__count">{monthData.moodCounts.normal}日</div>
                  <div className="mood-stats-item__label">普通の日</div>
                </div>
              )}

              {monthData.moodCounts.tired > 0 && (
                <div className="mood-stats-item mood-stats-item--tired">
                  <div className="mood-stats-item__emoji">
                    <img src="/emote/tiring.png" alt="疲れた" />
                  </div>
                  <div className="mood-stats-item__count">{monthData.moodCounts.tired}日</div>
                  <div className="mood-stats-item__label">休息が必要な日</div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    </AppLayout>
  );
};

export default MoodDetail;
